import React, { useState, useEffect } from 'react';
import { Calendar, User, CreditCard, LogOut, Palette } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from './ui/dropdown-menu';
import { DiscgoLogo } from './DiscgoLogo';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { themes } from '../lib/themes';
import { useTheme } from '../contexts/ThemeContext';
import { ThemeModal } from './ThemeModal';

interface HeaderProps {
  onNavigate: (page: 'dashboard' | 'bookings' | 'payment' | 'account', scrollToDistro?: boolean) => void;
}

interface UserProfile {
  first_name: string | null;
  last_name: string | null;
}

export default function Header({ onNavigate }: HeaderProps) {
  const { user, signOut } = useAuth();
  const { theme } = useTheme();
  const [isScrolled, setIsScrolled] = useState(false);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [themeModalOpen, setThemeModalOpen] = useState(false);

  const currentTheme = themes.find((t) => t.id === theme) ?? themes[0];

  useEffect(() => {
    if (user) {
      supabase
        .from('profiles')
        .select('first_name, last_name')
        .eq('id', user.id)
        .single()
        .then(({ data }) => {
          if (data) {
            setProfile(data);
          }
        });
    }
  }, [user]);

  const displayName = profile?.first_name && profile?.last_name
    ? `${profile.first_name} ${profile.last_name}`
    : user?.email?.split('@')[0] || 'User';

  const initials = profile?.first_name && profile?.last_name
    ? `${profile.first_name[0]}${profile.last_name[0]}`.toUpperCase()
    : displayName.split(' ').map((n: string) => n[0]).join('').toUpperCase().slice(0, 2);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled
            ? 'bg-white/95 backdrop-blur-sm shadow-sm border-b border-gray-200'
            : 'bg-[#F5F5DC]'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <div className="flex items-center">
              <DiscgoLogo style={{ height: '59px', width: '59px', cursor: 'pointer' }} onClick={() => onNavigate('dashboard')} />
            </div>

            {/* User Menu */}
            <div className="flex items-center space-x-4">
              <span className="text-sm text-gray-600">{displayName}</span>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <div className="h-8 w-8 bg-[#2B8B3B] rounded-full flex items-center justify-center text-white text-sm font-medium cursor-pointer hover:bg-[#236B2F] transition-colors">
                    {initials}
                  </div>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-52">
                  <DropdownMenuItem className="cursor-pointer" onClick={() => onNavigate('bookings')}>
                    <Calendar className="w-4 h-4 mr-2" />
                    Bookings
                  </DropdownMenuItem>
                  <DropdownMenuItem className="cursor-pointer" onClick={() => onNavigate('account')}>
                    <User className="w-4 h-4 mr-2" />
                    Account
                  </DropdownMenuItem>
                  <DropdownMenuItem className="cursor-pointer" onClick={() => onNavigate('payment')}>
                    <CreditCard className="w-4 h-4 mr-2" />
                    Payment
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem className="cursor-pointer" onClick={signOut}>
                    <LogOut className="w-4 h-4 mr-2" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </header>

      {/* ThemeModal hidden pre-launch — revisit post-launch */}
    </>
  );
}
